import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const { lang, t, clearCart } = useApp();
    const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
    const [orderId, setOrderId] = useState(null);
    const attemptsRef = useRef(0);
    const pollRef = useRef(null);

    useEffect(() => {
        const sessionId = params.get("session_id");
        if (!sessionId) { setStatus("failed"); return; }

        const poll = async () => {
            if (attemptsRef.current >= 10) {
                setStatus("timeout");
                return;
            }
            attemptsRef.current += 1;
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    setOrderId(r.data.order_id);
                    clearCart();
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("failed");
                    return;
                }
                pollRef.current = setTimeout(poll, 2000);
            } catch {
                pollRef.current = setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [params, clearCart]);

    return (
        <div data-testid="checkout-success-page" className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center min-h-[80vh] flex flex-col justify-center">
            {status === "checking" && (
                <>
                    <Loader2 size={48} className="mx-auto text-[#D4AF37] animate-spin mb-6" />
                    <h1 className="section-heading text-3xl text-white">{lang === "ar" ? "جارٍ التحقق من الدفع..." : "Verifying payment..."}</h1>
                </>
            )}
            {status === "paid" && (
                <>
                    <CheckCircle2 size={64} className="mx-auto text-[#D4AF37] mb-6" />
                    <div className="overline mb-3">{t.checkout.order_placed}</div>
                    <h1 className="section-heading text-4xl md:text-5xl text-white mb-4">{t.success.title}</h1>
                    <p className="text-white/70 mb-2">{t.success.subtitle}</p>
                    {orderId && <p className="text-white/50 text-sm mt-2">{t.checkout.order_number}: <span className="text-[#D4AF37]">{orderId}</span></p>}
                    <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/" className="btn-gold btn-gold-filled">{t.success.back_home}</Link>
                        <Link to="/products" className="btn-gold">{t.cart.continue}</Link>
                    </div>
                </>
            )}
            {(status === "failed" || status === "timeout") && (
                <>
                    <XCircle size={64} className="mx-auto text-red-400 mb-6" />
                    <h1 className="section-heading text-4xl text-white mb-4">{lang === "ar" ? "تعذر إتمام الدفع" : "Payment not completed"}</h1>
                    <Link to="/checkout" className="btn-gold mt-6 inline-flex">{lang === "ar" ? "إعادة المحاولة" : "Try again"}</Link>
                </>
            )}
        </div>
    );
}
